// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useState } from "react"
import {
  ApiError,
  appleSubscriptionOutlivesDeletion,
  type BillingSubscriptionResponse,
  type ExportJobResponse,
} from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { MoreSubpageHeader } from "@/components/layout/MoreSubpageHeader"
import { useAuth } from "@/hooks/useAuth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useI18n } from "@/i18n"
import {
  getAdsConsent,
  getMarketingConsent,
  onAdsConsentChange,
  onMarketingConsentChange,
  setAdsConsent,
  setMarketingConsent,
} from "@/lib/consentGate"
import { markHealthConsentWithdrawn, signalHealthConsentRequired, wasHealthConsentWithdrawn } from "@/lib/healthConsentGate"

/**
 * /app/more/privacy — T18 (ADR-021). Export runs synchronously server-side
 * and hands back a 15-minute download link. Deletion is gated twice: a
 * fresh password sign-in (the server rejects tokens with auth_time older
 * than 10 minutes) and an explicit typed confirmation. The press is ACCEPTED,
 * not completed (ADR-071): the server closes access immediately and finishes
 * the cleanup in the background, so this tab signs out locally and says the
 * deletion has started — never that it has finished.
 *
 * Health/AI consent (ADR-087, withdrawal added 2026-10-14): the person can withdraw the consent here in two taps
 * (button, then an explicit confirmation — the same effort as giving it: tick, then "Weiter"; GDPR Art. 7(3)).
 * The server closes the consent and erases the health data it covered in one transaction; the gate is raised at
 * once so any other route shows the consent screen again. The email route stays as the fallback.
 */
/** The email fallback for a consent withdrawal (operator-run; docs/operations/runbook.md "Withdrawal by email"). */
const PRIVACY_MAILTO = "mailto:privacy@bitebalance.de"

/** Apple's own subscription-management page (the same URL the Access page and the iPhone app open). */
const APPLE_MANAGE_SUBSCRIPTIONS_URL = "https://apps.apple.com/account/subscriptions"

export default function PrivacyPage() {
  const { t } = useI18n()
  const api = useApiClient()
  const { email, signIn, signOut } = useAuth()

  const [exportState, setExportState] = useState<"idle" | "working" | "error">("idle")
  const [exportResult, setExportResult] = useState<ExportJobResponse | null>(null)

  const [password, setPassword] = useState("")
  const [reauthed, setReauthed] = useState(false)
  const [confirmText, setConfirmText] = useState("")
  const [deleteState, setDeleteState] = useState<"idle" | "working" | "done" | "error">("idle")
  const [errorKey, setErrorKey] = useState<string | null>(null)

  // ADR-085 D7 / audit A-13: deleting the account does NOT cancel an Apple subscription (Apple bills it, not us). A user who pays
  // through Apple is told so before the confirmation — the same rule, helper and copy as the iPhone app. A missing row (404) or any
  // failure means no warning; the deletion flow itself is untouched (journey J27).
  const [sub, setSub] = useState<BillingSubscriptionResponse | null>(null)
  useEffect(() => {
    let cancelled = false
    api
      .getBillingSubscription()
      .then(info => {
        if (!cancelled) setSub(info)
      })
      .catch(() => {
        if (!cancelled) setSub(null)
      })
    return () => {
      cancelled = true
    }
  }, [api])

  const runExport = useCallback(async () => {
    setExportState("working")
    setExportResult(null)
    try {
      const job = await api.createExportJob()
      setExportResult(job)
      setExportState("idle")
    } catch {
      setExportState("error")
    }
  }, [api])

  const reauth = useCallback(async () => {
    if (!email || !password) return
    setErrorKey(null)
    try {
      await signIn(email, password)
      setReauthed(true)
      setPassword("")
    } catch {
      setErrorKey("privacy.wrongPassword")
    }
  }, [email, password, signIn])

  // Health/AI consent: read once, then driven by the withdrawal. "unknown" until /v1/me answers (and on a failed read —
  // the card then simply stays hidden, nothing here can produce a false "you have consented").
  const [healthConsent, setHealthConsent] = useState<"unknown" | "active" | "none">("unknown")
  const [withdrawStep, setWithdrawStep] = useState<"idle" | "confirm" | "working" | "done" | "error">(() =>
    wasHealthConsentWithdrawn() ? "done" : "idle"
  )
  useEffect(() => {
    let cancelled = false
    api
      .getMe()
      .then(me => {
        if (!cancelled) setHealthConsent(me.healthAiConsent ? "active" : "none")
      })
      .catch(() => {
        if (!cancelled) setHealthConsent("unknown")
      })
    return () => {
      cancelled = true
    }
  }, [api])

  const runWithdrawal = useCallback(async () => {
    setWithdrawStep("working")
    try {
      await api.deleteHealthAiConsent()
    } catch {
      setWithdrawStep("error")
      return
    }
    setHealthConsent("none")
    setWithdrawStep("done")
    // Every other route now shows the consent screen again (this one stays reachable, with the stripped frame, which
    // remounts the page: the flag lets the fresh page still say "you have withdrawn").
    markHealthConsentWithdrawn()
    signalHealthConsentRequired()
  }, [api])

  // Product analytics (PostHog): shows and changes the SAME choice the cookie banner records. Withdrawing
  // takes effect at once (lib/analytics.ts opts PostHog out in this session) — as easy as giving it.
  const [analyticsOn, setAnalyticsOn] = useState(() => getMarketingConsent() === "granted")
  useEffect(
    () =>
      onMarketingConsentChange(consent => {
        setAnalyticsOn(consent === "granted")
      }),
    []
  )

  // Marketing (Meta Pixel): the second, independent purpose — same wiring, its own key.
  const [adsOn, setAdsOn] = useState(() => getAdsConsent() === "granted")
  useEffect(
    () =>
      onAdsConsentChange(consent => {
        setAdsOn(consent === "granted")
      }),
    []
  )

  const confirmPhrase = t("privacy.deleteConfirmPhrase")

  const runDeletion = useCallback(async () => {
    setDeleteState("working")
    setErrorKey(null)
    try {
      await api.createDeletionJob({ confirmation: true })
      setDeleteState("done")
      // The request is accepted and access is already closed server-side;
      // drop the local session too. Cleanup finishes in the background, which
      // is what the copy on this screen now says (P1, 2026-09-11).
      window.setTimeout(() => void signOut(), 4000)
    } catch (err) {
      // A deletion that completed after a client timeout replays as 404 —
      // that IS the goodbye state, not a failure (M1, 2026-08-24 review).
      if (err instanceof ApiError && err.envelope.code === "USER_NOT_FOUND") {
        setDeleteState("done")
        window.setTimeout(() => void signOut(), 4000)
        return
      }
      setDeleteState("error")
      if (err instanceof ApiError && err.envelope.code === "REAUTH_REQUIRED") {
        setReauthed(false)
        setErrorKey("privacy.reauthExpired")
      } else {
        setErrorKey("privacy.deleteFailed")
      }
    }
  }, [api, signOut])

  if (deleteState === "done") {
    return (
      <section className="flex max-w-2xl flex-col gap-4">
        <h1 className="text-2xl font-bold text-ink">{t("privacy.deletedTitle")}</h1>
        <p className="text-sm text-ink-2">{t("privacy.deletedBody")}</p>
      </section>
    )
  }

  return (
    <section aria-labelledby="privacy-heading" className="flex max-w-2xl flex-col gap-6">
      <MoreSubpageHeader title={t("privacy.title")} headingId="privacy-heading" />

      <div className="rounded-bb-xl border border-line bg-paper p-5">
        <p className="font-semibold text-ink">{t("privacy.exportTitle")}</p>
        <p className="mt-1 text-sm text-ink-2">{t("privacy.exportBody")}</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            type="button"
            className="min-h-11"
            disabled={exportState === "working"}
            onClick={() => void runExport()}
          >
            {exportState === "working" ? t("privacy.exportWorking") : t("privacy.exportButton")}
          </Button>
          {exportResult?.downloadUrl && (
            <a
              href={exportResult.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-green underline underline-offset-2"
            >
              {t("privacy.exportDownload")}
            </a>
          )}
        </div>
        {exportResult?.downloadUrl && (
          <p className="mt-2 text-xs text-ink-2">{t("privacy.exportExpiry")}</p>
        )}
        {exportState === "error" && (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {t("privacy.exportFailed")}
          </p>
        )}
      </div>

      {/* Always rendered: the email route must stay visible even when /v1/me could not be read. */}
      <div
        className="flex flex-col gap-3 rounded-bb-xl border border-line bg-paper p-5"
        data-testid="health-consent-card"
      >
        <p className="font-semibold text-ink">{t("privacy.healthConsentTitle")}</p>
        {healthConsent === "unknown" ? null : healthConsent === "active" ? (
          <>
            <p className="text-sm text-ink-2">{t("privacy.healthConsentActive")}</p>
            {withdrawStep === "idle" || withdrawStep === "error" ? (
              <Button
                type="button"
                variant="outline"
                className="min-h-11 self-start"
                onClick={() => setWithdrawStep("confirm")}
              >
                {t("privacy.healthConsentWithdraw")}
              </Button>
            ) : (
              <div
                role="group"
                aria-label={t("privacy.healthConsentWithdraw")}
                className="flex flex-col gap-3 rounded-bb-md border border-destructive/40 bg-destructive/5 p-4"
              >
                <p className="text-sm text-ink">{t("privacy.healthConsentWithdrawConfirmBody")}</p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="destructive"
                    className="min-h-11"
                    disabled={withdrawStep === "working"}
                    onClick={() => void runWithdrawal()}
                  >
                    {withdrawStep === "working"
                      ? t("privacy.healthConsentWithdrawing")
                      : t("privacy.healthConsentWithdrawConfirm")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11"
                    disabled={withdrawStep === "working"}
                    onClick={() => setWithdrawStep("idle")}
                  >
                    {t("privacy.healthConsentKeep")}
                  </Button>
                </div>
              </div>
            )}
            {withdrawStep === "error" && (
              <p role="alert" className="text-sm text-destructive">
                {t("privacy.healthConsentWithdrawError")}
              </p>
            )}
          </>
        ) : (
          <p role={withdrawStep === "done" ? "status" : undefined} className="text-sm text-ink-2">
            {withdrawStep === "done"
              ? t("privacy.healthConsentWithdrawn")
              : t("privacy.healthConsentNone")}
          </p>
        )}
        <p className="text-sm text-ink-2">
          {t("privacy.consentWithdrawLead")}{" "}
          <a
            href={PRIVACY_MAILTO}
            className="inline-flex min-h-11 items-center font-semibold text-ink underline underline-offset-2 hover:text-green"
          >
            {t("privacy.consentWithdrawLink")}
          </a>
        </p>
      </div>

      <div className="flex flex-col gap-4 rounded-bb-xl border border-line bg-paper p-5">
        <div className="flex min-h-11 items-start gap-3">
          <input
            id="privacy-analytics"
            type="checkbox"
            role="switch"
            checked={analyticsOn}
            aria-describedby="privacy-analytics-help"
            onChange={e => setMarketingConsent(e.target.checked ? "granted" : "denied")}
            className="mt-0.5 size-5 shrink-0 rounded border-line text-green focus-visible:ring-2 focus-visible:ring-green"
          />
          <label htmlFor="privacy-analytics" className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-ink">{t("privacy.analyticsLabel")}</span>
            <span id="privacy-analytics-help" className="text-sm text-ink-2">
              {t("privacy.analyticsHelp")}
            </span>
          </label>
        </div>
        <div className="flex min-h-11 items-start gap-3">
          <input
            id="privacy-ads"
            type="checkbox"
            role="switch"
            checked={adsOn}
            aria-describedby="privacy-ads-help"
            onChange={e => setAdsConsent(e.target.checked ? "granted" : "denied")}
            className="mt-0.5 size-5 shrink-0 rounded border-line text-green focus-visible:ring-2 focus-visible:ring-green"
          />
          <label htmlFor="privacy-ads" className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-ink">{t("privacy.adsLabel")}</span>
            <span id="privacy-ads-help" className="text-sm text-ink-2">
              {t("privacy.adsHelp")}
            </span>
          </label>
        </div>
      </div>

      <div className="rounded-bb-xl border border-destructive/40 bg-destructive/5 p-5">
        <p className="font-semibold text-ink">{t("privacy.deleteTitle")}</p>
        <p className="mt-1 text-sm text-ink-2">{t("privacy.deleteBody")}</p>
        {appleSubscriptionOutlivesDeletion(sub) && (
          <p role="note" className="mt-3 text-sm text-ink">
            {t("privacy.appleSubscriptionWarning")}{" "}
            <a
              href={APPLE_MANAGE_SUBSCRIPTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-2"
            >
              {t("privacy.appleSubscriptionManageLink")}
            </a>
          </p>
        )}

        {!reauthed ? (
          <div className="mt-3 flex flex-col gap-2">
            <label htmlFor="privacy-password" className="text-sm font-medium text-ink">
              {t("privacy.reauthLabel")}
            </label>
            <div className="flex gap-2">
              <Input
                id="privacy-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                className="min-h-11 shrink-0"
                disabled={!password}
                onClick={() => void reauth()}
              >
                {t("privacy.reauthButton")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            <label htmlFor="privacy-confirm" className="text-sm font-medium text-ink">
              {t("privacy.deleteConfirmLabel", { phrase: confirmPhrase })}
            </label>
            <Input
              id="privacy-confirm"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder={confirmPhrase}
            />
            <Button
              type="button"
              variant="destructive"
              className="min-h-11 self-start"
              disabled={confirmText !== confirmPhrase || deleteState === "working"}
              onClick={() => void runDeletion()}
            >
              {deleteState === "working" ? t("privacy.deleteWorking") : t("privacy.deleteButton")}
            </Button>
          </div>
        )}
        {errorKey && (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {t(errorKey)}
          </p>
        )}
      </div>
    </section>
  )
}
